import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import posthog from "posthog-js";
import React, { useEffect, useRef, useState } from "react";

type Testimonial = {
    quote: string;
    author?: string;
    source?: string;
    sourceLink?: string;
};

type PatientTestimonialsProps = {
    headline: string;
    testimonials: Testimonial[];
};

const PatientTestimonials = ({
    headline,
    testimonials,
}: PatientTestimonialsProps) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [currentSection, setCurrentSection] = useState(1);
    const [itemsPerSection, setItemsPerSection] = useState(3);
    const [totalSections, setTotalSections] = useState(
        Math.ceil(testimonials.length / itemsPerSection),
    );
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const updateLayout = () => {
            const isMobileView = window.innerWidth < 1024; // Using lg breakpoint
            setIsMobile(isMobileView);

            if (!isMobileView && scrollContainerRef.current) {
                const newItemsPerSection = 3; // Always show 3 items per section on desktop

                setItemsPerSection(newItemsPerSection);
                setTotalSections(
                    Math.ceil(testimonials.length / newItemsPerSection),
                );
            }
        };

        updateLayout();
        window.addEventListener("resize", updateLayout);

        return () => window.removeEventListener("resize", updateLayout);
    }, [testimonials.length]);

    const scroll = (direction: "left" | "right") => {
        if (scrollContainerRef.current) {
            posthog?.capture("patient_testimonial_scroll", {
                direction,
                currentSection,
                totalSections,
            });
            const containerWidth = scrollContainerRef.current.offsetWidth;
            const scrollAmount =
                direction === "left" ? -containerWidth : containerWidth;

            if (direction === "left" && currentSection > 1) {
                setCurrentSection(currentSection - 1);
            } else if (
                direction === "right" &&
                currentSection < totalSections
            ) {
                setCurrentSection(currentSection + 1);
            }

            scrollContainerRef.current.scrollBy({
                left: scrollAmount,
                behavior: "smooth",
            });
        }
    };

    const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => (
        <div className="h-full bg-muted rounded-2xl p-6 flex flex-col justify-between">
            <div className="text-[#333333] dark:text-white text-xl md:text-2xl font-normal font-satoshi leading-relaxed mb-6">
                {testimonial.quote}
            </div>
            <div className="flex items-center gap-2">
                <span className="opacity-50 text-[#333333] dark:text-gray-300 text-lg font-normal font-space">
                    {testimonial.author}
                </span>
                {testimonial.sourceLink ? (
                    <a
                        href={testimonial.sourceLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1a5b77] dark:text-blue-300 text-lg font-normal font-space underline"
                    >
                        {testimonial.source}
                    </a>
                ) : (
                    <span className="text-[#1a5b77] dark:text-blue-300 text-lg font-normal font-space">
                        {testimonial.source}
                    </span>
                )}
            </div>
        </div>
    );

    const shouldShowNavigation = totalSections > 1 && !isMobile;

    return (
        <div className="w-full relative z-10">
            <div className="flex flex-col items-center gap-8">
                {/* Title */}
                <h2 className="text-[#333333] dark:text-white text-3xl md:text-5xl font-semibold font-satoshi uppercase leading-tight text-center">
                    {headline}
                </h2>

                {/* Mobile Layout - Stacked Cards */}
                <div className="flex flex-col gap-6 lg:hidden w-full">
                    {testimonials.map((testimonial, index) => (
                        <div key={index} className="w-full">
                            <TestimonialCard testimonial={testimonial} />
                        </div>
                    ))}
                </div>

                {/* Desktop Layout - Carousel */}
                <div className="relative w-full hidden lg:block">
                    {shouldShowNavigation && (
                        <div className="flex items-center justify-between mx-10 mb-8">
                            {/* Progress Bar */}
                            <div className="flex items-center gap-4">
                                {Array.from(
                                    { length: totalSections },
                                    (_, i) => (
                                        <div
                                            key={i}
                                            className={`h-2 w-10 rounded-md ${
                                                currentSection === i + 1
                                                    ? "bg-[#1a5b77] dark:bg-blue-300"
                                                    : "bg-gray-200 dark:bg-gray-600"
                                            }`}
                                        />
                                    ),
                                )}
                            </div>
                            {/* Navigation Buttons */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => scroll("left")}
                                    className="text-[#1a5b77] dark:text-blue-300 disabled:opacity-50"
                                    disabled={currentSection === 1}
                                    aria-label="Scroll Left"
                                >
                                    <ChevronLeftIcon className="w-8 h-8" />
                                </button>
                                <button
                                    onClick={() => scroll("right")}
                                    className="text-[#1a5b77] dark:text-blue-300 disabled:opacity-50"
                                    disabled={currentSection === totalSections}
                                    aria-label="Scroll Right"
                                >
                                    <ChevronRightIcon className="w-8 h-8" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Cards Container */}
                    <div
                        ref={scrollContainerRef}
                        className="overflow-hidden px-6"
                    >
                        <div
                            className="flex transition-transform duration-300 ease-in-out gap-6"
                            style={{
                                transform: `translateX(-${(currentSection - 1) * 100}%)`,
                            }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div
                                    key={index}
                                    className="w-[calc(33.333%-16px)] shrink-0"
                                >
                                    <TestimonialCard
                                        testimonial={testimonial}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientTestimonials;
